/** XeAgents.ai - CRM Dashboard for Incoming Agents **/
import { useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { format, isValid } from "date-fns";
import { formatRelativeTime } from "@/lib/date-format";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { DataPagination, usePagination } from "@/components/ui/data-pagination";
import {
  Search,
  Download,
  Loader2,
  Phone,
  Calendar,
  Clock,
  Users,
  PhoneIncoming,
  PhoneOutgoing,
  TrendingUp,
  MessageSquare,
  Play,
  Pause,
  Eye,
  Edit,
  Sparkles,
  FileText,
  Mic,
  Bot,
  User,
  Mail,
  Filter,
  RefreshCw
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { AuthStorage } from "@/lib/auth-storage";
import { CustomerReportDialog, type CustomerReport } from "@/components/CustomerReportDialog";

interface CRMDashboard {
  stats: {
    totalCalls: number;
    totalDuration: number;
    completedCalls: number;
    avgDuration: number;
    uniqueCallers: number;
    totalAgents: number;
    incomingCalls: number;
    outgoingCalls: number;
  };
  callsByDate: Array<{ date: string; count: number; duration: number }>;
  callsByAgent: Array<{ agentId: string; agentName: string; totalCalls: number; totalDuration: number }>;
  sentimentBreakdown: Array<{ sentiment: string; count: number }>;
  recentCalls: Array<{
    id: string;
    phoneNumber: string;
    status: string;
    duration: number;
    sentiment: string;
    aiSummary: string;
    createdAt: string;
    agentId: string;
    agentName: string;
  }>;
  agents: Array<{ id: string; name: string }>;
}

interface CRMCall {
  id: string;
  phoneNumber: string;
  status: string;
  callDirection: string;
  duration: number;
  recordingUrl: string | null;
  transcript: string | null;
  aiSummary: string | null;
  classification: string | null;
  sentiment: string | null;
  metadata: Record<string, any> | null;
  wasTransferred: boolean;
  transferredTo: string | null;
  startedAt: string | null;
  endedAt: string | null;
  createdAt: string;
  elevenLabsConversationId: string | null;
  agentId: string | null;
  agentName: string | null;
  agentSystemPrompt: string | null;
  phoneNumberId: string | null;
  phoneNumberDisplay: string | null;
  // Qualification scoring fields
  callOutcome: string | null;
  qualificationScore: number | null;
  qualificationDecision: 'STRONG_FIT' | 'GOOD_FIT' | 'MAYBE' | 'NOT_FIT' | null;
  dealBreakersResult: { passed: boolean; failedOn: string[] } | null;
  scoringBreakdown: Record<string, number> | null;
}

interface CRMContact {
  id: string;
  phoneNumber: string;
  name: string | null;
  email: string | null;
  firstCallDate: string;
  lastCallDate: string;
  totalCalls: number;
  totalDuration: number;
  sentiment: string | null;
  agentId: string | null;
  agentName: string | null;
}

interface ContactDetail {
  phoneNumber: string;
  name: string | null;
  email: string | null;
  firstCallDate: string;
  lastCallDate: string;
  totalCalls: number;
  totalDuration: number;
  averageDuration: number;
  sentiment: string | null;
  calls: Array<{
    id: string;
    status: string;
    duration: number;
    transcript: string | null;
    aiSummary: string | null;
    sentiment: string | null;
    createdAt: string;
    agentName: string | null;
    recordingUrl: string | null;
    elevenLabsConversationId: string | null;
  }>;
}

export default function CRM() {
  const { t } = useTranslation();
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  
  // Filters
  const [searchQuery, setSearchQuery] = useState("");
  const [agentFilter, setAgentFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sentimentFilter, setSentimentFilter] = useState("all");
  const [directionFilter, setDirectionFilter] = useState("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  
  // Contact detail modal
  const [selectedContact, setSelectedContact] = useState<string | null>(null);
  const [editingContact, setEditingContact] = useState<{ name: string; email: string; notes: string } | null>(null);
  
  // Audio playback
  const [playingCallId, setPlayingCallId] = useState<string | null>(null);
  const [loadingRecording, setLoadingRecording] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Customer report
  const [customerReportOpen, setCustomerReportOpen] = useState(false);
  const [selectedReport, setSelectedReport] = useState<CustomerReport | null>(null);
  const [generatingReportFor, setGeneratingReportFor] = useState<string | null>(null);

  // Fetch CRM dashboard
  const dashboardParams = new URLSearchParams();
  if (directionFilter !== 'all') dashboardParams.set('direction', directionFilter);
  const dashboardUrl = `/api/crm/dashboard${dashboardParams.toString() ? `?${dashboardParams.toString()}` : ''}`;

  const { data: dashboard, isLoading: dashboardLoading } = useQuery<CRMDashboard>({
    queryKey: [dashboardUrl],
  });

  // Fetch CRM calls
  const callsParams = new URLSearchParams();
  if (searchQuery) callsParams.set('search', searchQuery);
  if (agentFilter !== 'all') callsParams.set('agentId', agentFilter);
  if (statusFilter !== 'all') callsParams.set('status', statusFilter);
  if (sentimentFilter !== 'all') callsParams.set('sentiment', sentimentFilter);
  if (directionFilter !== 'all') callsParams.set('direction', directionFilter);
  if (startDate) callsParams.set('startDate', startDate);
  if (endDate) callsParams.set('endDate', endDate);
  const callsUrl = `/api/crm/calls${callsParams.toString() ? `?${callsParams.toString()}` : ''}`;
  
  const { data: callsResponse, isLoading: callsLoading } = useQuery<{ data: CRMCall[]; pagination: any }>({
    queryKey: [callsUrl],
  });

  // Fetch CRM contacts
  const contactsParams = new URLSearchParams();
  if (searchQuery) contactsParams.set('search', searchQuery);
  if (agentFilter !== 'all') contactsParams.set('agentId', agentFilter);
  if (directionFilter !== 'all') contactsParams.set('direction', directionFilter);
  if (startDate) contactsParams.set('startDate', startDate);
  if (endDate) contactsParams.set('endDate', endDate);
  const contactsUrl = `/api/crm/contacts${contactsParams.toString() ? `?${contactsParams.toString()}` : ''}`;
  
  const { data: contactsResponse, isLoading: contactsLoading } = useQuery<{ data: CRMContact[]; pagination: any }>({
    queryKey: [contactsUrl],
  });

  // Fetch contact detail
  const { data: contactDetail, isLoading: contactDetailLoading } = useQuery<ContactDetail>({
    queryKey: [`/api/crm/contact/${encodeURIComponent(selectedContact || '')}`],
    enabled: !!selectedContact,
  });

  // Update contact mutation
  const updateContactMutation = useMutation({
    mutationFn: async ({ phoneNumber, data }: { phoneNumber: string; data: { name?: string; email?: string; notes?: string } }) => {
      const response = await apiRequest("PUT", `/api/crm/contact/${encodeURIComponent(phoneNumber)}`, data);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/crm/contacts"] });
      queryClient.invalidateQueries({ queryKey: ["/api/crm/contact", selectedContact] });
      toast({ title: "Contact updated", description: "Contact information has been saved." });
      setEditingContact(null);
    },
    onError: (error: any) => {
      toast({ title: "Update failed", description: error.message, variant: "destructive" });
    },
  });

  const formatDuration = (seconds: number | null) => {
    if (!seconds) return "0:00";
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const formatCallDate = (dateString: string | null | undefined) => {
    if (!dateString) return "Unknown date";
    const date = new Date(dateString);
    if (!isValid(date)) return "Unknown date";
    return format(date, "MMM d, h:mm a");
  };

  const formatTotalDuration = (seconds: number) => {
    if (!seconds) return "0m";
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    if (hours > 0) return `${hours}h ${mins}m`;
    return `${mins}m`;
  };

  const getSentimentBadge = (sentiment: string | null) => {
    if (!sentiment) return null;
    switch (sentiment) {
      case "positive":
        return <Badge className="bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/20">Positive</Badge>;
      case "negative":
        return <Badge className="bg-rose-500/10 text-rose-700 dark:text-rose-400 border-rose-500/20">Negative</Badge>;
      case "neutral":
        return <Badge className="bg-slate-500/10 text-slate-700 dark:text-slate-400 border-slate-500/20">Neutral</Badge>;
      default:
        return <Badge variant="outline">{sentiment}</Badge>;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "completed":
        return <Badge className="bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/20">Completed</Badge>;
      case "failed":
        return <Badge className="bg-rose-500/10 text-rose-700 dark:text-rose-400 border-rose-500/20">Failed</Badge>;
      case "in_progress":
      case "in-progress":
        return <Badge className="bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/20">In Progress</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getDirectionBadge = (direction: string | null) => {
    if (direction === 'incoming' || direction === 'inbound') {
      return (
        <Badge className="bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/20 flex items-center gap-1">
          <PhoneIncoming className="h-3 w-3" />
          Inbound
        </Badge>
      );
    }
    if (direction === 'outgoing' || direction === 'outbound') {
      return (
        <Badge className="bg-blue-500/10 text-blue-700 dark:text-blue-400 border-blue-500/20 flex items-center gap-1">
          <PhoneOutgoing className="h-3 w-3" />
          Outbound
        </Badge>
      );
    }
    return null;
  };

  // Qualification decision badge for HR screening / lead qualification
  const getQualificationBadge = (decision: string | null, score: number | null) => {
    if (!decision) return null;
    const scoreText = score !== null ? ` (${score}/100)` : '';
    switch (decision) {
      case "STRONG_FIT":
        return (
          <Badge className="bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/20">
            Strong Fit{scoreText}
          </Badge>
        );
      case "GOOD_FIT":
        return (
          <Badge className="bg-teal-500/10 text-teal-700 dark:text-teal-400 border-teal-500/20">
            Good Fit{scoreText}
          </Badge>
        );
      case "MAYBE":
        return (
          <Badge className="bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/20">
            Maybe{scoreText}
          </Badge>
        );
      case "NOT_FIT":
        return (
          <Badge className="bg-rose-500/10 text-rose-700 dark:text-rose-400 border-rose-500/20">
            Not a Fit{scoreText}
          </Badge>
        );
      default:
        return <Badge variant="outline">{decision}</Badge>;
    }
  };

  // Helper to extract contact info from metadata
  const getContactInfoFromMetadata = (metadata: Record<string, any> | null) => {
    if (!metadata) return { name: null, email: null };
    return {
      name: metadata.callerName || metadata.name || metadata.contactName || null,
      email: metadata.callerEmail || metadata.email || metadata.contactEmail || null,
    };
  };

  // Helper to truncate call script for display
  const truncateCallScript = (script: string | null, maxLength = 150) => {
    if (!script) return null;
    if (script.length <= maxLength) return script;
    return script.slice(0, maxLength) + "...";
  };

  const hasRecording = (call: CRMCall) => {
    const isCallCompleted = call.status === 'completed' || call.status === 'answered' || call.status === 'ended';
    return !!call.recordingUrl || !!(call.elevenLabsConversationId && isCallCompleted);
  };

  const handlePlayRecording = async (e: React.MouseEvent, call: CRMCall) => {
    e.stopPropagation();
    
    if (playingCallId === call.id) {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      setPlayingCallId(null);
      return;
    }
    
    if (audioRef.current) {
      audioRef.current.pause();
    }
    
    setLoadingRecording(call.id);
    
    try {
      const headers: Record<string, string> = {};
      const authHeader = AuthStorage.getAuthHeader();
      if (authHeader) {
        headers["Authorization"] = authHeader;
      }
      
      const response = await fetch(`/api/calls/${call.id}/recording`, {
        headers,
        credentials: "include",
      });
      
      if (!response.ok) {
        throw new Error("Failed to fetch recording");
      }
      
      const blob = await response.blob();
      const audioUrl = URL.createObjectURL(blob);
      
      const audio = new Audio(audioUrl);
      audioRef.current = audio;
      setPlayingCallId(call.id);
      setLoadingRecording(null);
      
      audio.onended = () => {
        setPlayingCallId(null);
        audioRef.current = null;
        URL.revokeObjectURL(audioUrl);
      };
      
      await audio.play();
    } catch (error: any) {
      setLoadingRecording(null);
      toast({
        title: "Recording Unavailable",
        description: error.message || "Could not load recording.",
        variant: "destructive",
      });
      setPlayingCallId(null);
    }
  };

  const handleExport = async () => {
    try {
      const params = new URLSearchParams();
      if (agentFilter !== "all") params.set("agentId", agentFilter);
      if (directionFilter !== "all") params.set("direction", directionFilter);
      if (startDate) params.set("startDate", startDate);
      if (endDate) params.set("endDate", endDate);
      
      const headers: Record<string, string> = {};
      const authHeader = AuthStorage.getAuthHeader();
      if (authHeader) {
        headers["Authorization"] = authHeader;
      }
      
      const response = await fetch(`/api/crm/export?${params.toString()}`, {
        headers,
        credentials: "include",
      });
      
      if (!response.ok) throw new Error("Export failed");
      
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `crm-export-${new Date().toISOString().split("T")[0]}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      
      toast({ title: "Export complete", description: "CRM data has been downloaded." });
    } catch (error: any) {
      toast({ title: "Export failed", description: error.message, variant: "destructive" });
    }
  };

  const calls = callsResponse?.data || [];
  const contacts = contactsResponse?.data || [];
  const callsPagination = usePagination(calls, 10);
  const contactsPagination = usePagination(contacts, 10);

  if (dashboardLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-purple-50 via-indigo-50 to-blue-50 dark:from-purple-950/40 dark:via-indigo-950/30 dark:to-blue-950/40 border p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center shadow-lg">
              <Users className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-foreground">CRM Dashboard</h1>
              <p className="text-muted-foreground text-sm">Auto-captured incoming call contacts and history</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => queryClient.invalidateQueries({ queryKey: ["/api/crm"] })} className="bg-background/80">
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
            <Button variant="default" onClick={handleExport}>
              <Download className="h-4 w-4 mr-2" />
              Export CSV
            </Button>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mt-6">
          <div className="bg-background/60 backdrop-blur-sm rounded-lg p-3 border">
            <div className="flex items-center gap-2">
              <Phone className="h-4 w-4 text-purple-600 dark:text-purple-400" />
              <span className="text-2xl font-bold text-foreground">{dashboard?.stats.totalCalls || 0}</span>
            </div>
            <p className="text-xs text-muted-foreground mt-1">Total Calls</p>
          </div>
          <div className="bg-background/60 backdrop-blur-sm rounded-lg p-3 border">
            <div className="flex items-center gap-2">
              <PhoneIncoming className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
              <span className="text-2xl font-bold text-foreground">{dashboard?.stats.incomingCalls || 0}</span>
            </div>
            <p className="text-xs text-muted-foreground mt-1">Inbound</p>
          </div>
          <div className="bg-background/60 backdrop-blur-sm rounded-lg p-3 border">
            <div className="flex items-center gap-2">
              <PhoneOutgoing className="h-4 w-4 text-blue-600 dark:text-blue-400" />
              <span className="text-2xl font-bold text-foreground">{dashboard?.stats.outgoingCalls || 0}</span>
            </div>
            <p className="text-xs text-muted-foreground mt-1">Outbound</p>
          </div>
          <div className="bg-background/60 backdrop-blur-sm rounded-lg p-3 border">
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-amber-600 dark:text-amber-400" />
              <span className="text-2xl font-bold text-foreground">{formatTotalDuration(dashboard?.stats.totalDuration || 0)}</span>
            </div>
            <p className="text-xs text-muted-foreground mt-1">Total Duration</p>
          </div>
          <div className="bg-background/60 backdrop-blur-sm rounded-lg p-3 border">
            <div className="flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-indigo-600 dark:text-indigo-400" />
              <span className="text-2xl font-bold text-foreground">{formatDuration(dashboard?.stats.avgDuration || 0)}</span>
            </div>
            <p className="text-xs text-muted-foreground mt-1">Avg Duration</p>
          </div>
          <div className="bg-background/60 backdrop-blur-sm rounded-lg p-3 border">
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 text-rose-600 dark:text-rose-400" />
              <span className="text-2xl font-bold text-foreground">{dashboard?.stats.uniqueCallers || 0}</span>
            </div>
            <p className="text-xs text-muted-foreground mt-1">Unique Contacts</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by phone number, transcript..."
            className="pl-9"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <Select value={agentFilter} onValueChange={setAgentFilter}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="All Agents" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Agents</SelectItem>
            {dashboard?.agents.map((agent) => (
              <SelectItem key={agent.id} value={agent.id}>{agent.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[140px]">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
            <SelectItem value="failed">Failed</SelectItem>
            <SelectItem value="in_progress">In Progress</SelectItem>
          </SelectContent>
        </Select>
        <Select value={sentimentFilter} onValueChange={setSentimentFilter}>
          <SelectTrigger className="w-[140px]">
            <SelectValue placeholder="Sentiment" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Sentiment</SelectItem>
            <SelectItem value="positive">Positive</SelectItem>
            <SelectItem value="neutral">Neutral</SelectItem>
            <SelectItem value="negative">Negative</SelectItem>
          </SelectContent>
        </Select>
        <Select value={directionFilter} onValueChange={setDirectionFilter}>
          <SelectTrigger className="w-[140px]">
            <SelectValue placeholder="Direction" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Directions</SelectItem>
            <SelectItem value="incoming">
              <span className="flex items-center gap-2">
                <PhoneIncoming className="h-3 w-3" />
                Inbound
              </span>
            </SelectItem>
            <SelectItem value="outgoing">
              <span className="flex items-center gap-2">
                <PhoneOutgoing className="h-3 w-3" />
                Outbound
              </span>
            </SelectItem>
          </SelectContent>
        </Select>
        <Input
          type="date"
          className="w-[150px]"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          placeholder="Start Date"
        />
        <Input
          type="date"
          className="w-[150px]"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          placeholder="End Date"
        />
      </div>

      {/* Main Content Tabs */}
      <Tabs defaultValue="calls" className="space-y-4">
        <TabsList>
          <TabsTrigger value="calls">
            <PhoneIncoming className="h-4 w-4 mr-2" />
            Calls ({calls.length})
          </TabsTrigger>
          <TabsTrigger value="contacts">
            <Users className="h-4 w-4 mr-2" />
            Contacts ({contacts.length})
          </TabsTrigger>
          <TabsTrigger value="agents">
            <Bot className="h-4 w-4 mr-2" />
            Agents ({dashboard?.agents.length || 0})
          </TabsTrigger>
        </TabsList>

        {/* Calls Tab */}
        <TabsContent value="calls" className="space-y-3">
          {callsLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : calls.length === 0 ? (
            <Card className="p-12 text-center">
              <PhoneIncoming className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
              <h3 className="font-medium text-lg mb-1">No incoming calls yet</h3>
              <p className="text-muted-foreground text-sm">
                Incoming calls from your agents will appear here automatically.
              </p>
            </Card>
          ) : (
            <>
              <div className="grid gap-3">
                {callsPagination.paginatedItems.map((call) => {
                  const contactInfo = getContactInfoFromMetadata(call.metadata);
                  return (
                    <div
                      key={call.id}
                      className="group bg-card border rounded-lg overflow-hidden hover:shadow-lg transition-all duration-200 cursor-pointer"
                      onClick={() => setLocation(`/app/calls/${call.id}`)}
                    >
                      <div className="flex">
                        <div className={`w-1 ${
                          call.callDirection === 'incoming' || call.callDirection === 'inbound'
                            ? 'bg-emerald-500'
                            : call.callDirection === 'outgoing' || call.callDirection === 'outbound'
                              ? 'bg-blue-500'
                              : call.status === "completed" ? "bg-emerald-500" : call.status === "failed" ? "bg-rose-500" : "bg-amber-500"
                        }`} />
                        <div className="flex-1 p-4">
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex items-start gap-3 min-w-0">
                              <div className={`flex-shrink-0 h-10 w-10 rounded-full flex items-center justify-center ${
                                call.callDirection === 'incoming' || call.callDirection === 'inbound'
                                  ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
                                  : call.callDirection === 'outgoing' || call.callDirection === 'outbound'
                                    ? 'bg-blue-500/10 text-blue-600 dark:text-blue-400'
                                    : 'bg-purple-500/10 text-purple-600 dark:text-purple-400'
                              }`}>
                                {contactInfo.name ? (
                                  <span className="text-sm font-bold">{contactInfo.name[0].toUpperCase()}</span>
                                ) : call.callDirection === 'outgoing' || call.callDirection === 'outbound' ? (
                                  <PhoneOutgoing className="h-4 w-4" />
                                ) : (
                                  <PhoneIncoming className="h-4 w-4" />
                                )}
                              </div>
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2 flex-wrap">
                                  {contactInfo.name && (
                                    <h3 className="font-semibold text-foreground">
                                      {contactInfo.name}
                                    </h3>
                                  )}
                                  <span className={`${contactInfo.name ? "text-sm text-muted-foreground" : "font-semibold text-foreground"} font-mono`}>
                                    {call.phoneNumber || `Call ${call.id.slice(0, 8)}`}
                                  </span>
                                  {getDirectionBadge(call.callDirection)}
                                  {getStatusBadge(call.status)}
                                  {getSentimentBadge(call.sentiment)}
                                  {getQualificationBadge(call.qualificationDecision, call.qualificationScore)}
                                </div>
                                <div className="flex items-center gap-3 mt-1 text-sm text-muted-foreground flex-wrap">
                                  {contactInfo.email && (
                                    <span className="flex items-center gap-1">
                                      <Mail className="h-3 w-3" />
                                      {contactInfo.email}
                                    </span>
                                  )}
                                  {call.agentName && (
                                    <span className="flex items-center gap-1">
                                      <Bot className="h-3 w-3" />
                                      {call.agentName}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              {hasRecording(call) && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-9 w-9 rounded-full"
                                  onClick={(e) => handlePlayRecording(e, call)}
                                  disabled={loadingRecording === call.id}
                                >
                                  {loadingRecording === call.id ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                  ) : playingCallId === call.id ? (
                                    <Pause className="h-4 w-4 text-primary" />
                                  ) : (
                                    <Play className="h-4 w-4" />
                                  )}
                                </Button>
                              )}
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-4 mt-3 flex-wrap">
                            <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                              <Clock className="h-3.5 w-3.5" />
                              <span className="font-medium">{formatDuration(call.duration)}</span>
                            </div>
                            <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                              <Calendar className="h-3.5 w-3.5" />
                              <span>{formatCallDate(call.createdAt)}</span>
                            </div>
                            {call.phoneNumberDisplay && (
                              <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                                <Phone className="h-3.5 w-3.5" />
                                <span>Agent: {call.phoneNumberDisplay}</span>
                              </div>
                            )}
                          </div>

                          {/* AI Summary Section */}
                          {call.aiSummary && (
                            <div className="mt-3 pt-3 border-t border-border/50">
                              <div className="flex items-start gap-2">
                                <Sparkles className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                                <div>
                                  <p className="text-xs font-medium text-primary mb-1">AI Summary</p>
                                  <p className="text-sm text-muted-foreground">{call.aiSummary}</p>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Transcript Preview Section */}
                          {!call.aiSummary && call.transcript && (
                            <div className="mt-3 pt-3 border-t border-border/50">
                              <div className="flex items-start gap-2">
                                <MessageSquare className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                                <div>
                                  <p className="text-xs font-medium text-muted-foreground mb-1">Transcript Preview</p>
                                  <p className="text-sm text-muted-foreground line-clamp-2">{call.transcript}</p>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Call Script Section */}
                          {call.agentSystemPrompt && (
                            <div className="mt-3 pt-3 border-t border-border/50">
                              <div className="flex items-start gap-2">
                                <FileText className="h-4 w-4 text-indigo-500 mt-0.5 flex-shrink-0" />
                                <div>
                                  <p className="text-xs font-medium text-indigo-600 dark:text-indigo-400 mb-1">Agent Script</p>
                                  <p className="text-sm text-muted-foreground line-clamp-2">{truncateCallScript(call.agentSystemPrompt)}</p>
                                </div>
                              </div>
                            </div>
                          )}
                          
                          <div className="flex items-center justify-between gap-2 mt-3">
                            <div className="flex items-center gap-2 flex-wrap">
                              {hasRecording(call) && (
                                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                  <Mic className="h-3 w-3" />
                                  <span>Recording</span>
                                </div>
                              )}
                              {call.transcript && (
                                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                  <FileText className="h-3 w-3" />
                                  <span>Transcript</span>
                                </div>
                              )}
                              {call.aiSummary && (
                                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                  <Sparkles className="h-3 w-3" />
                                  <span>AI Summary</span>
                                </div>
                              )}
                              {call.agentSystemPrompt && (
                                <div className="flex items-center gap-1 text-xs text-indigo-600 dark:text-indigo-400">
                                  <Bot className="h-3 w-3" />
                                  <span>Script</span>
                                </div>
                              )}
                            </div>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                setLocation(`/app/calls/${call.id}`);
                              }}
                            >
                              <Eye className="h-4 w-4 mr-1" />
                              View Details
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <DataPagination
                currentPage={callsPagination.currentPage}
                totalPages={callsPagination.totalPages}
                totalItems={callsPagination.totalItems}
                itemsPerPage={callsPagination.itemsPerPage}
                onPageChange={callsPagination.handlePageChange}
                onItemsPerPageChange={callsPagination.handleItemsPerPageChange}
              />
            </>
          )}
        </TabsContent>

        {/* Contacts Tab */}
        <TabsContent value="contacts" className="space-y-3">
          {contactsLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : contacts.length === 0 ? (
            <Card className="p-12 text-center">
              <Users className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
              <h3 className="font-medium text-lg mb-1">No contacts yet</h3>
              <p className="text-muted-foreground text-sm">
                Contacts are automatically captured from incoming calls.
              </p>
            </Card>
          ) : (
            <>
              <div className="grid gap-3">
                {contactsPagination.paginatedItems.map((contact) => (
                  <Card
                    key={contact.id}
                    className="cursor-pointer hover:shadow-lg transition-all duration-200"
                    onClick={() => setSelectedContact(contact.phoneNumber)}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="h-10 w-10 rounded-full bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center text-white font-bold">
                            {contact.name ? contact.name[0].toUpperCase() : <User className="h-5 w-5" />}
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <h3 className="font-semibold">{contact.name || "Unknown"}</h3>
                              {getSentimentBadge(contact.sentiment)}
                            </div>
                            <p className="text-sm text-muted-foreground font-mono">{contact.phoneNumber}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="flex items-center gap-4">
                            <div className="text-center">
                              <p className="text-lg font-bold">{contact.totalCalls}</p>
                              <p className="text-xs text-muted-foreground">Calls</p>
                            </div>
                            <div className="text-center">
                              <p className="text-lg font-bold">{formatTotalDuration(contact.totalDuration)}</p>
                              <p className="text-xs text-muted-foreground">Duration</p>
                            </div>
                          </div>
                          <p className="text-xs text-muted-foreground mt-1">
                            Last: {contact.lastCallDate && isValid(new Date(contact.lastCallDate))
                              ? formatRelativeTime(contact.lastCallDate)
                              : "Unknown"}
                          </p>
                        </div>
                      </div>
                      {contact.agentName && (
                        <div className="flex items-center gap-1 mt-2 text-xs text-muted-foreground">
                          <Bot className="h-3 w-3" />
                          <span>Last handled by: {contact.agentName}</span>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
              <DataPagination
                currentPage={contactsPagination.currentPage}
                totalPages={contactsPagination.totalPages}
                totalItems={contactsPagination.totalItems}
                itemsPerPage={contactsPagination.itemsPerPage}
                onPageChange={contactsPagination.handlePageChange}
                onItemsPerPageChange={contactsPagination.handleItemsPerPageChange}
              />
            </>
          )}
        </TabsContent>

        {/* Agents Tab */}
        <TabsContent value="agents" className="space-y-3">
          {dashboard?.callsByAgent && dashboard.callsByAgent.length > 0 ? (
            <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
              {dashboard.callsByAgent.map((agent) => (
                <Card key={agent.agentId} className="hover:shadow-lg transition-all duration-200">
                  <CardHeader className="pb-2">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
                        <Bot className="h-5 w-5 text-white" />
                      </div>
                      <div>
                        <CardTitle className="text-base">{agent.agentName || "Unknown Agent"}</CardTitle>
                        <CardDescription>Incoming Agent</CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-2xl font-bold">{agent.totalCalls}</p>
                        <p className="text-xs text-muted-foreground">Total Calls</p>
                      </div>
                      <div>
                        <p className="text-2xl font-bold">{formatTotalDuration(agent.totalDuration)}</p>
                        <p className="text-xs text-muted-foreground">Total Duration</p>
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full mt-4"
                      onClick={() => setAgentFilter(agent.agentId)}
                    >
                      <Filter className="h-4 w-4 mr-2" />
                      View Calls
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="p-12 text-center">
              <Bot className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
              <h3 className="font-medium text-lg mb-1">No incoming agents</h3>
              <p className="text-muted-foreground text-sm">
                Create an incoming agent to start receiving calls.
              </p>
              <Button className="mt-4" onClick={() => setLocation("/app/agents")}>
                Create Agent
              </Button>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Contact Detail Dialog */}
      <Dialog open={!!selectedContact} onOpenChange={(open) => !open && setSelectedContact(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              Contact Details
            </DialogTitle>
            <DialogDescription>
              View and edit contact information
            </DialogDescription>
          </DialogHeader>
          
          {contactDetailLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : contactDetail ? (
            <div className="space-y-6">
              {/* Contact Info */}
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-12 w-12 rounded-full bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center text-white font-bold text-lg">
                    {contactDetail.name ? contactDetail.name[0].toUpperCase() : <User className="h-6 w-6" />}
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg">{contactDetail.name || "Unknown"}</h3>
                    <p className="text-muted-foreground font-mono">{contactDetail.phoneNumber}</p>
                    {contactDetail.email && (
                      <p className="text-sm text-muted-foreground flex items-center gap-1">
                        <Mail className="h-3 w-3" />
                        {contactDetail.email}
                      </p>
                    )}
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setEditingContact({
                    name: contactDetail.name || "",
                    email: contactDetail.email || "",
                    notes: "",
                  })}
                >
                  <Edit className="h-4 w-4 mr-1" />
                  Edit
                </Button>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-4 gap-4">
                <div className="text-center p-3 bg-muted/50 rounded-lg">
                  <p className="text-2xl font-bold">{contactDetail.totalCalls}</p>
                  <p className="text-xs text-muted-foreground">Total Calls</p>
                </div>
                <div className="text-center p-3 bg-muted/50 rounded-lg">
                  <p className="text-2xl font-bold">{formatTotalDuration(contactDetail.totalDuration)}</p>
                  <p className="text-xs text-muted-foreground">Total Duration</p>
                </div>
                <div className="text-center p-3 bg-muted/50 rounded-lg">
                  <p className="text-2xl font-bold">{formatDuration(contactDetail.averageDuration)}</p>
                  <p className="text-xs text-muted-foreground">Avg Duration</p>
                </div>
                <div className="text-center p-3 bg-muted/50 rounded-lg">
                  {getSentimentBadge(contactDetail.sentiment) || <Badge variant="outline">Unknown</Badge>}
                  <p className="text-xs text-muted-foreground mt-1">Sentiment</p>
                </div>
              </div>

              {/* Call History */}
              <div>
                <h4 className="font-medium mb-3">Call History</h4>
                <div className="space-y-2 max-h-[300px] overflow-y-auto">
                  {contactDetail.calls.map((call) => (
                    <div
                      key={call.id}
                      className="p-3 border rounded-lg hover:bg-muted/50 cursor-pointer"
                      onClick={() => setLocation(`/app/calls/${call.id}`)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          {getStatusBadge(call.status)}
                          {getSentimentBadge(call.sentiment)}
                          <span className="text-sm font-medium">{formatDuration(call.duration)}</span>
                        </div>
                        <span className="text-xs text-muted-foreground">
                          {formatCallDate(call.createdAt)}
                        </span>
                      </div>
                      {call.aiSummary && (
                        <p className="text-sm text-muted-foreground mt-2 line-clamp-2">{call.aiSummary}</p>
                      )}
                      {call.agentName && (
                        <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                          <Bot className="h-3 w-3" />
                          {call.agentName}
                        </p>
                      )}
                      {(call.recordingUrl || (call.elevenLabsConversationId && (call.status === 'completed' || call.status === 'answered' || call.status === 'ended'))) && (
                        <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                          <Mic className="h-3 w-3" />
                          <span>Recording</span>
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Edit Contact Dialog */}
      <Dialog open={!!editingContact} onOpenChange={(open) => !open && setEditingContact(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Contact</DialogTitle>
            <DialogDescription>Update contact information for {selectedContact}</DialogDescription>
          </DialogHeader>
          {editingContact && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  value={editingContact.name}
                  onChange={(e) => setEditingContact({ ...editingContact, name: e.target.value })}
                  placeholder="Contact name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={editingContact.email}
                  onChange={(e) => setEditingContact({ ...editingContact, email: e.target.value })}
                  placeholder="contact@example.com"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="notes">Notes</Label>
                <Textarea
                  id="notes"
                  value={editingContact.notes}
                  onChange={(e) => setEditingContact({ ...editingContact, notes: e.target.value })}
                  placeholder="Add notes about this contact..."
                  rows={3}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingContact(null)}>Cancel</Button>
            <Button
              onClick={() => {
                if (selectedContact && editingContact) {
                  updateContactMutation.mutate({
                    phoneNumber: selectedContact,
                    data: editingContact,
                  });
                }
              }}
              disabled={updateContactMutation.isPending}
            >
              {updateContactMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
